import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { materializePickerFile, useWidgetModulesOptional } from '@teo/widget-core';
import { useWidget } from '../context/WidgetContext.js';
import { useChatEmojiPicker } from './ChatEmojiPicker.js';
import { AiPoweredFooter } from './AiPoweredFooter.js';
import { IconAttach, IconMic, IconSend } from './icons.js';

const INPUT_LINE_HEIGHT_PX = 20;
const INPUT_MAX_LINES = 5;
const INPUT_MAX_HEIGHT_PX = INPUT_LINE_HEIGHT_PX * INPUT_MAX_LINES;

type ChatInputProps = {
  onSend?: (text: string) => void;
  onImageSelect?: (file: File) => void;
  isRecording?: boolean;
  isUploadingImage?: boolean;
  /**
   * Image attach requires a backend `thread_id` (upload URL is thread-scoped).
   * When false, paperclip + file input are hidden.
   */
  canAttachImage?: boolean;
  onToggleRecording?: () => void;
};

export const ChatInput = memo(function ChatInput({
  onSend,
  onImageSelect,
  isRecording = false,
  isUploadingImage = false,
  canAttachImage = true,
  onToggleRecording,
}: ChatInputProps) {
  const { t } = useWidget();
  const modules = useWidgetModulesOptional();
  const fileUploadEnabled =
    (modules?.isEnabled('fileUpload') ?? true) && canAttachImage;
  const voiceEnabled = modules?.isEnabled('voiceMessages') ?? false;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const prevValueLenRef = useRef(0);
  const fileInputId = 'teo-chat-image-upload';
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);

  const measureCollapse = useCallback(() => {
    const el = textInputRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.min(el.scrollHeight, INPUT_MAX_HEIGHT_PX)}px`;
  }, []);

  /**
   * Autosize without forcing layout on every keystroke.
   * Grow when content overflows; only collapse-measure when text shrinks.
   * Blind `height: 0` + scrollHeight on each letter blocks paint (input lag).
   */
  useLayoutEffect(() => {
    const el = textInputRef.current;
    if (!el) return;

    const shrinking = value.length < prevValueLenRef.current;
    prevValueLenRef.current = value.length;

    if (!shrinking) {
      if (el.scrollHeight <= el.clientHeight + 1) {
        return;
      }
      const next = Math.min(el.scrollHeight, INPUT_MAX_HEIGHT_PX);
      if (next === el.clientHeight) return;
      el.style.height = `${next}px`;
      return;
    }

    measureCollapse();
  }, [value, measureCollapse]);

  useEffect(() => {
    measureCollapse();
  }, [isRecording, measureCollapse]);

  const handleSend = () => {
    const text = value.trim();
    if (!text) return;
    onSend?.(text);
    setValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const insertEmoji = useCallback((emoji: string) => {
    const input = textInputRef.current;
    setValue((current) => {
      if (!input) return current + emoji;
      const start = input.selectionStart ?? current.length;
      const end = input.selectionEnd ?? current.length;
      const next = current.slice(0, start) + emoji + current.slice(end);
      const caret = start + emoji.length;
      requestAnimationFrame(() => {
        input.focus();
        input.setSelectionRange(caret, caret);
      });
      return next;
    });
  }, []);

  const openFilePicker = useCallback(() => {
    if (!fileUploadEnabled || isUploadingImage || isRecording) return;
    fileInputRef.current?.click();
  }, [fileUploadEnabled, isUploadingImage, isRecording]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    if (!picked) {
      event.target.value = '';
      return;
    }

    // iOS Safari: clear the input only AFTER bytes are in the JS heap.
    // Clearing first (or awaiting elsewhere first) can upload 0-byte files.
    void (async () => {
      try {
        const file = await materializePickerFile(picked);
        event.target.value = '';
        if (file.size === 0) return;
        onImageSelect?.(file);
      } catch {
        event.target.value = '';
      }
    })();
  };

  const busy = (voiceEnabled && isRecording) || isUploadingImage;
  const emojiPicker = useChatEmojiPicker({
    disabled: busy,
    onSelect: insertEmoji,
    inputRef: textInputRef,
  });

  return (
    <div className="teo-input-stack" ref={emojiPicker.rootRef}>
      {emojiPicker.panel}
      <div className="teo-input-bar">
        {fileUploadEnabled ? (
          <>
            <label className="teo-input__file" htmlFor={fileInputId}>
              {t.attachFile}
            </label>
            <input
              id={fileInputId}
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
              className="teo-input__file"
              tabIndex={-1}
              aria-label={t.attachFile}
              title={t.attachFile}
              onChange={handleFileChange}
            />

            <button
              type="button"
              className={`teo-icon-btn teo-icon-btn--input teo-attach-btn${isUploadingImage ? ' teo-attach-btn--busy' : ''}`}
              aria-label={t.attachFile}
              onClick={openFilePicker}
              disabled={busy}
            >
              {isUploadingImage ? (
                <span className="teo-attach-btn__spinner" aria-hidden />
              ) : (
                <IconAttach />
              )}
            </button>
          </>
        ) : null}

        {emojiPicker.button}

        {voiceEnabled && isRecording ? (
          <div className="teo-recording">
            <span className="teo-recording__dot" />
            {t.recording}
          </div>
        ) : (
          <div
            className={`teo-input-wrap${focused ? ' teo-input-wrap--focused' : ''}`}
          >
            <textarea
              ref={textInputRef}
              className="teo-input"
              rows={1}
              placeholder={t.inputPlaceholder}
              title={t.inputPlaceholder}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              autoComplete="off"
              enterKeyHint="send"
              disabled={isUploadingImage}
            />
          </div>
        )}

        {voiceEnabled && (
          <button
            type="button"
            className="teo-icon-btn teo-icon-btn--input"
            aria-label={t.sendVoice}
            onClick={onToggleRecording}
            disabled={isUploadingImage}
          >
            <IconMic />
          </button>
        )}

        <button
          type="button"
          className="teo-send-btn"
          onClick={handleSend}
          aria-label="Send"
          disabled={isUploadingImage}
        >
          <IconSend />
        </button>
      </div>
      <AiPoweredFooter />
    </div>
  );
});
